'use client';

import { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Employee } from './types';

interface DocumentUploadModalProps {
  employee: Employee | null;
  onClose: () => void;
  onUploaded: () => void;
}

const DOCUMENT_TYPES: Array<{ key: string; label: string; group: string }> = [
  { key: 'offer_letter', label: 'Offer Letter', group: 'Onboarding' },
  { key: 'i9', label: 'I-9 Verification', group: 'Onboarding' },
  { key: 'w4', label: 'W-4 Form', group: 'Onboarding' },
  { key: 'direct_deposit', label: 'Direct Deposit Form', group: 'Onboarding' },
  { key: 'handbook_acknowledgement', label: 'Handbook Acknowledgement', group: 'Onboarding' },
  { key: 'noncompete', label: 'Non-Compete Agreement', group: 'Legal' },
  { key: 'nda', label: 'NDA', group: 'Legal' },
  { key: 'osha_certification', label: 'OSHA Certification', group: 'Certifications' },
  { key: 'manufacturer_certification', label: 'Manufacturer Cert', group: 'Certifications' },
  { key: 'state_license', label: 'State License', group: 'Certifications' },
  { key: 'drivers_license', label: "Driver's License", group: 'Certifications' },
  { key: 'medical_clearance', label: 'Medical Clearance', group: 'Health' },
  { key: 'background_check', label: 'Background Check', group: 'Compliance' },
  { key: 'drug_test', label: 'Drug Test', group: 'Compliance' },
  { key: 'performance_review', label: 'Performance Review', group: 'Performance' },
  { key: 'disciplinary_action', label: 'Disciplinary Action', group: 'Performance' },
  { key: 'training_certificate', label: 'Training Certificate', group: 'Training' },
  { key: 'other', label: 'Other', group: 'Other' },
];

const DocumentUploadModal = ({ employee, onClose, onUploaded }: DocumentUploadModalProps) => {
  const [docType, setDocType] = useState('offer_letter');
  const [displayName, setDisplayName] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const supabase = createClient();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!employee) {
      setDocType('offer_letter');
      setDisplayName('');
      setExpirationDate('');
      setNotes('');
      setSelectedFile(null);
      setSubmitting(false);
      setProgress(0);
    }
  }, [employee?.id]);

  useEffect(() => {
    if (!employee) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [employee, submitting, onClose]);

  if (!employee) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File must be under 25MB');
      return;
    }
    setSelectedFile(file);
    if (!displayName) setDisplayName(file.name.replace(/\.[^.]+$/, ''));
  };

  const isValid = !!selectedFile && displayName.trim().length >= 2 && !!docType && !submitting;

  const handleUpload = async () => {
    if (!isValid || !selectedFile) return;
    setSubmitting(true);
    setProgress(0);

    // Safe JSON parser — throws a readable error if the server returns HTML (e.g. 502)
    const safeJson = async (resp: Response, label: string) => {
      const ct = resp.headers.get('content-type') ?? '';
      if (!ct.includes('application/json')) {
        throw new Error(`${label} returned unexpected response (HTTP ${resp.status}). Please try again.`);
      }
      return resp.json();
    };

    try {
      const sigResp = await fetch('/api/hr/document-upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: employee.id,
          filename: selectedFile.name,
          content_type: selectedFile.type,
        }),
      });
      const sigData = await safeJson(sigResp, 'Upload URL endpoint');
      if (!sigResp.ok) throw new Error(sigData.error ?? 'Failed to get upload URL');

      setProgress(30);

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', sigData.signed_url);
        xhr.setRequestHeader('Content-Type', selectedFile.type);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            const pct = 30 + Math.round((ev.loaded / ev.total) * 60);
            setProgress(pct);
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed with status ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('Upload network error'));
        xhr.send(selectedFile);
      });

      setProgress(95);

      // Insert metadata row with timeout + 429 retry + verbose logging
      const reporterName = `${currentUser?.first_name ?? ''} ${currentUser?.last_name ?? ''}`.trim();

      const insertPayload = {
        employee_id: employee.id,
        document_type: docType,
        display_name: displayName.trim(),
        storage_path: sigData.storage_path,
        file_size_bytes: selectedFile.size,
        mime_type: selectedFile.type,
        expiration_date: expirationDate || null,
        uploaded_by_name: reporterName || 'Unknown',
        notes: notes.trim() || null,
      };

      console.log('[DocUpload] Storage upload complete. Attempting metadata INSERT:', insertPayload);

      const insertWithRetry = async (): Promise<void> => {
        for (let attempt = 0; attempt < 3; attempt++) {
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error(`Insert timed out (attempt ${attempt + 1}/3)`)), 8000)
          );
          const insertPromise = supabase
            .from('employee_documents')
            .insert(insertPayload)
            .select()
            .single()
            .then((res: any) => {
              if (res.error) {
                console.error(`[DocUpload] Insert error attempt ${attempt + 1}:`, res.error);
                throw res.error;
              }
              console.log(`[DocUpload] Insert succeeded attempt ${attempt + 1}:`, res.data);
              return res.data;
            });

          try {
            await Promise.race([insertPromise, timeoutPromise]);
            return;
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            const isRateLimit = msg.includes('429') || msg.includes('rate') || msg.toLowerCase().includes('too many');
            const isTimeout = msg.includes('timed out');
            console.warn(`[DocUpload] Attempt ${attempt + 1} failed: ${msg}`);
            if (attempt === 2 || (!isRateLimit && !isTimeout)) {
              throw err;
            }
            const backoff = 500 * Math.pow(2, attempt);
            console.log(`[DocUpload] Backing off ${backoff}ms before retry…`);
            await new Promise(r => setTimeout(r, backoff));
          }
        }
      };

      try {
        await insertWithRetry();
      } catch (err) {
        console.error('[DocUpload] Insert ultimately failed after retries:', err);
        toast.error(
          `File uploaded but metadata save failed: ${err instanceof Error ? err.message : 'Unknown'}. ` +
          `Try again in a moment, or have an admin clean up the orphaned file in Supabase Storage.`,
          { duration: 8000 }
        );
        setSubmitting(false);
        setProgress(0);
        return;
      }

      setProgress(100);
      toast.success(`${displayName.trim()} uploaded`, { icon: '📄', duration: 3000 });
      onUploaded();
      setSubmitting(false);
      onClose();
    } catch (err) {
      console.error('Document upload failed:', err);
      toast.error(`Upload failed: ${err instanceof Error ? err.message : 'Unknown'}`);
      setSubmitting(false);
      setProgress(0);
    }
  };

  const groupedTypes = DOCUMENT_TYPES.reduce((acc, opt) => {
    if (!acc[opt.group]) acc[opt.group] = [];
    acc[opt.group].push(opt);
    return acc;
  }, {} as Record<string, typeof DOCUMENT_TYPES>);

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[120] flex items-center justify-center p-4"
      onClick={submitting ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="doc-upload-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div>
            <h3 id="doc-upload-title" className="text-lg font-bold text-[#1C2E5E]">Upload Document</h3>
            <p className="text-[12px] text-gray-500 mt-0.5">{employee.first_name} {employee.last_name}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 disabled:opacity-50" aria-label="Close">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">
              Document Type <span className="text-red-500">*</span>
            </label>
            <select
              value={docType}
              onChange={e => setDocType(e.target.value)}
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all"
            >
              {Object.entries(groupedTypes).map(([group, opts]) => (
                <optgroup key={group} label={group}>
                  {opts.map(opt => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">
              Display Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="e.g. 2026 Updated I-9"
              maxLength={120}
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">
              Expiration Date <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              type="date"
              value={expirationDate}
              onChange={e => setExpirationDate(e.target.value)}
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">
              Notes <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Internal notes about this document"
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all resize-none"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">
              File <span className="text-red-500">*</span>
            </label>
            {selectedFile ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <path d="M14 2v6h6"/>
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-semibold truncate">{selectedFile.name}</p>
                  <p className="text-[10px] text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedFile(null)}
                  disabled={submitting}
                  className="text-[11px] text-red-600 hover:underline flex-shrink-0 disabled:opacity-50"
                >
                  Remove
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full p-6 rounded-lg border-2 border-dashed border-gray-300 hover:border-[#E89A3C] hover:bg-blue-50 transition-all text-center text-gray-500 hover:text-[#E89A3C]"
              >
                <p className="text-[12px] font-semibold">Click to select file</p>
                <p className="text-[10px] text-gray-400 mt-1">PDF, Word, or image up to 25MB</p>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {submitting && progress > 0 && (
            <div>
              <div className="flex items-center justify-between text-[11px] mb-1">
                <span className="text-gray-600">Uploading…</span>
                <span className="font-semibold">{progress}%</span>
              </div>
              <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full bg-[#E89A3C] transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={!isValid}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-[#1C2E5E] hover:bg-[#022b80] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Uploading…' : 'Upload Document'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;
